import {
  AiSearchIcon,
  AudienceIcon,
  AvMediaSettingsIcon,
  AVSourcesIcon,
  BrandIcon,
  CalendarRecorderIcon,
  LocationIcon,
  MediaFilesIcon,
  MediaStationIcon,
  NewsMediaIcon,
  RecIcon,
  RelevantPeopleIcon,
  ReportCNMCIcon,
  ReviewValidationIcon,
  ScheduleEmisionIcon,
  ValueIcon,
  VideoBangFranjaIcon,
  VideoCutsCortesIcon,
} from '@acceso/design-system';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { PERMISSIONS, PERMISSIONS_MENU } from '@/lib/auth';
import { AVRouteEnum } from '@/routes/constants/AVRouteEnum';
import { MediaManagerRouteEnum } from '@/routes/constants/MediaManagerRouteEnum';

import { TMenu, TMenuItem } from '../types';

const useMediaManagerMenu = (): (TMenuItem | TMenu)[] => {
  const { t } = useTranslation();

  const mediaManagerMenu: (TMenuItem | TMenu)[] = useMemo(
    () => [
      {
        icon: AVSourcesIcon,
        name: t('Fuentes'),
        routeName: AVRouteEnum.SOURCES,
        permissions: PERMISSIONS_MENU.SOURCES,
        submenu: [
          {
            icon: LocationIcon,
            name: t('Centros de producción'),
            routeName: AVRouteEnum.PRODUCTION_CENTERS,
            permissions: [PERMISSIONS.MEDIA_MANAGER.VIEW_PRODUCTION_CENTER],
          },
          {
            icon: MediaStationIcon,
            name: t('Emisoras madre'),
            routeName: AVRouteEnum.PARENT_BROADCASTERS,
            permissions: [PERMISSIONS.MEDIA_MANAGER.VIEW_CHANNEL],
          },
          {
            icon: NewsMediaIcon,
            name: t('Emisoras'),
            routeName: AVRouteEnum.BROADCASTERS,
            permissions: [PERMISSIONS.MEDIA_MANAGER.VIEW_CHANNEL],
          },
        ],
      },
      {
        icon: AvMediaSettingsIcon,
        name: t('Gestión AV'),
        routeName: AVRouteEnum.MANAGEMENT,
        permissions: PERMISSIONS_MENU.MANAGEMENT,
        submenu: [
          {
            icon: CalendarRecorderIcon,
            name: t('Programar grabación'),
            routeName: AVRouteEnum.SCHEDULE_RECORDINGS,
            permissions: [PERMISSIONS.MEDIA_MANAGER.VIEW_CHANNEL_RECORDING_SCHEDULE],
          },
          {
            icon: RecIcon,
            name: t('Grabaciones'),
            routeName: AVRouteEnum.RECORDINGS,
            permissions: [PERMISSIONS.MEDIA_MANAGER.VIEW_CHANNEL_RECORDING],
          },
          {
            icon: MediaFilesIcon,
            name: t('Programas'),
            routeName: AVRouteEnum.PROGRAMS,
            permissions: [PERMISSIONS.MEDIA_MANAGER.VIEW_PROGRAM],
          },
        ],
      },
      {
        icon: AiSearchIcon,
        name: t('Enriquecimiento'),
        routeName: AVRouteEnum.ENRICHMENTS,
        permissions: PERMISSIONS_MENU.ENRICHMENTS,
        submenu: [
          {
            icon: RelevantPeopleIcon,
            name: t('Personas de interés'),
            routeName: AVRouteEnum.FEATURED_PEOPLE,
            permissions: [PERMISSIONS.MEDIA_MANAGER.VIEW_PERSON],
          },
          {
            icon: BrandIcon,
            name: t('Logos'),
            routeName: AVRouteEnum.LOGOS,
            permissions: [PERMISSIONS.MEDIA_MANAGER.VIEW_LOGO],
          },
          {
            icon: AVSourcesIcon,
            name: t('Soportes'),
            routeName: AVRouteEnum.SUPPORTS,
            permissions: [PERMISSIONS.MEDIA_MANAGER.VIEW_LOGO],
          },
          {
            icon: AudienceIcon,
            name: t('Audiencias'),
            routeName: AVRouteEnum.AUDIENCES,
            permissions: [PERMISSIONS.MEDIA_MANAGER.VIEW_AUDIENCE_DATA],
            submenu: [
              {
                icon: AVSourcesIcon,
                name: t('Fuentes de audiencia'),
                routeName: AVRouteEnum.AUDIENCE_CHANNEL_SOURCES,
                permissions: [PERMISSIONS.MEDIA_MANAGER.VIEW_AUDIENCE_DATA],
              },
              {
                icon: AudienceIcon,
                name: t('Audiencias'),
                routeName: AVRouteEnum.AUDIENCES_LIST,
                permissions: [PERMISSIONS.MEDIA_MANAGER.VIEW_AUDIENCE_DATA],
              },
            ],
          },
          {
            icon: ValueIcon,
            name: t('Valor económico'),
            routeName: AVRouteEnum.ECONOMIC_VALUE,
            permissions: [PERMISSIONS.MEDIA_MANAGER.VIEW_ECONOMIC_VALUE_DATA],
            submenu: [
              {
                icon: AVSourcesIcon,
                name: t('Fuentes de valor económico'),
                routeName: AVRouteEnum.ECONOMIC_VALUE_CHANNEL_SOURCES,
                permissions: [PERMISSIONS.MEDIA_MANAGER.VIEW_ECONOMIC_VALUE_DATA],
              },
              {
                icon: ValueIcon,
                name: t('Valor económico'),
                routeName: AVRouteEnum.ECONOMIC_VALUE_UPLOAD,
                permissions: [PERMISSIONS.MEDIA_MANAGER.VIEW_ECONOMIC_VALUE_DATA],
              },
            ],
          },
          {
            icon: ReportCNMCIcon,
            name: t('Informe CNMC'),
            routeName: AVRouteEnum.CNMC_REPORTS,
            permissions: [PERMISSIONS.MEDIA_MANAGER.VIEW_CNMC_REPORT],
          },
        ],
      },
      {
        icon: ReviewValidationIcon,
        name: t('Revision'),
        routeName: MediaManagerRouteEnum.REVIEW,
        permissions: PERMISSIONS_MENU.REVIEW,
        submenu: [
          {
            icon: ScheduleEmisionIcon,
            name: t('Parrilla emisión'),
            routeName: MediaManagerRouteEnum.BROADCAST_SCHEDULE,
            permissions: [PERMISSIONS.MEDIA_MANAGER.VIEW_PROGRAM_BROADCAST],
          },
          {
            icon: VideoBangFranjaIcon,
            name: t('Franjas'),
            routeName: MediaManagerRouteEnum.SEGMENTS,
            permissions: [PERMISSIONS.MEDIA_MANAGER.VIEW_SEGMENT],
          },
          {
            icon: VideoCutsCortesIcon,
            name: t('Cortes'),
            routeName: MediaManagerRouteEnum.CLIPS,
            permissions: [PERMISSIONS.MEDIA_MANAGER.VIEW_CLIP],
          },
        ],
      },
    ],
    [t]
  );

  return mediaManagerMenu;
};

export { useMediaManagerMenu };
